import {
  isConnectorDetailSnapshot,
  isConnectorRegistrySnapshot,
  type ConnectorDetailSnapshot,
  type ConnectorRegistrySnapshot,
  type ConnectorStatus,
} from "@whitehaven/contracts";
import {
  canAccessPortalArea,
  type AuthorizationSubject,
} from "@whitehaven/permissions";
import { Card, ContentState, StatusPill } from "@whitehaven/ui";
import { useEffect, useState } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import { ConnectorConfigurationPanel } from "./ConnectorConfigurationPanel";
import type { AdminConnectorSettingsGateways } from "./admin-connector-settings-gateway";
import {
  ADMIN_CONNECTORS_PATH,
  resolveAdminConnectorSettingsRoute,
  type AdminConnectorSettingsRoute as ResolvedSettingsRoute,
} from "./admin-connector-settings-model";

export interface AdminConnectorSettingsRouteProps {
  readonly pathname: string;
  readonly subject: AuthorizationSubject | null;
  readonly gateways: AdminConnectorSettingsGateways | null;
}

type LoadState =
  | { readonly kind: "loading" }
  | ({ readonly kind: "error" } & LoadIdentity)
  | ({
      readonly kind: "registry";
      readonly value: ConnectorRegistrySnapshot;
    } & LoadIdentity)
  | ({
      readonly kind: "detail";
      readonly value: ConnectorDetailSnapshot;
    } & LoadIdentity);

interface LoadIdentity {
  readonly subject: AuthorizationSubject;
  readonly pathname: string;
  readonly gateways: AdminConnectorSettingsGateways;
  readonly refreshEpoch: number;
}

function currentLoadState(
  state: LoadState,
  subject: AuthorizationSubject | null,
  pathname: string,
  gateways: AdminConnectorSettingsGateways | null,
  refreshEpoch: number,
): LoadState {
  if (
    state.kind !== "loading" &&
    state.subject === subject &&
    state.pathname === pathname &&
    state.gateways === gateways &&
    state.refreshEpoch === refreshEpoch
  ) {
    return state;
  }
  return { kind: "loading" };
}

const statusLabels: Readonly<Record<ConnectorStatus, string>> = {
  DISABLED: "Disabled",
  NEEDS_CONFIGURATION: "Needs configuration",
  ACTION_REQUIRED: "Action required",
  TESTING: "Testing",
  SIMULATED: "Simulated",
  HEALTHY: "Healthy",
  DEGRADED: "Degraded",
  FAILED: "Failed",
  BLOCKED: "Blocked",
  REVOKED: "Revoked",
};

function statusTone(
  status: ConnectorStatus,
): "neutral" | "info" | "positive" | "warning" | "critical" {
  if (status === "HEALTHY") return "positive";
  if (status === "DISABLED") return "neutral";
  if (status === "TESTING") return "info";
  if (status === "FAILED" || status === "BLOCKED" || status === "REVOKED") {
    return "critical";
  }
  return "warning";
}

function canReadConnectorSettings(subject: AuthorizationSubject | null) {
  return (
    subject !== null &&
    canAccessPortalArea(subject, "admin-settings", "view")
  );
}

function SettingsFrame({ children }: { readonly children: React.ReactNode }) {
  return (
    <section
      className="portal-page admin-settings"
      aria-labelledby="portal-title"
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">Administration</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Settings &amp; connections
          </h1>
          <p>
            Secret-free organization configuration and server-derived connector
            status. Business-unit overrides are unavailable in this increment.
          </p>
        </div>
      </header>
      <nav className="admin-settings__nav" aria-label="Settings workspace">
        <a href="/admin/settings">Overview</a>
        <a href={ADMIN_CONNECTORS_PATH}>Connectors</a>
      </nav>
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

function CompatibilityNotice({
  route,
}: {
  readonly route: ResolvedSettingsRoute;
}) {
  if (route.compatibility === "integrations") {
    return (
      <ContentState kind="warning" headingLevel={2} title="Integrations moved">
        <p>
          This compatibility route now displays the canonical connector registry
          at <a href={ADMIN_CONNECTORS_PATH}>{ADMIN_CONNECTORS_PATH}</a>.
        </p>
      </ContentState>
    );
  }
  if (route.compatibility === "auth") {
    return (
      <ContentState
        kind="warning"
        headingLevel={2}
        title="Authentication settings moved"
      >
        <p>Firebase identity status is now part of the connector registry.</p>
      </ContentState>
    );
  }
  return null;
}

function ConnectorCards({
  registry,
}: {
  readonly registry: ConnectorRegistrySnapshot;
}) {
  return (
    <div
      className="admin-settings__connector-grid"
      data-connector-count={registry.connectors.length}
    >
      {registry.connectors.map((connector) => (
        <Card
          key={connector.connectorId}
          headingLevel={2}
          eyebrow={connector.category}
          title={connector.name}
          description={connector.description}
          actions={
            <StatusPill tone={statusTone(connector.status)}>
              {statusLabels[connector.status]}
            </StatusPill>
          }
          footer={
            <a href={`${ADMIN_CONNECTORS_PATH}/${connector.connectorId}`}>
              View safe details
            </a>
          }
        >
          <dl className="admin-settings__facts">
            <div>
              <dt>Provider</dt>
              <dd>{connector.provider}</dd>
            </div>
            <div>
              <dt>Environment</dt>
              <dd>{connector.environment}</dd>
            </div>
            <div>
              <dt>Version</dt>
              <dd>{connector.configurationVersion ?? "Not configured"}</dd>
            </div>
          </dl>
        </Card>
      ))}
    </div>
  );
}

function ConnectorDetail({
  detail,
  subject,
  gateways,
  notice,
  onMutationConfirmed,
  onRefreshRequested,
}: {
  readonly detail: ConnectorDetailSnapshot;
  readonly subject: AuthorizationSubject;
  readonly gateways: AdminConnectorSettingsGateways;
  readonly notice: string | null;
  readonly onMutationConfirmed: (message: string) => void;
  readonly onRefreshRequested: () => void;
}) {
  const { connector } = detail;
  return (
    <article className="admin-settings__detail">
      <a href={ADMIN_CONNECTORS_PATH}>Back to connectors</a>
      <header>
        <p>{connector.category}</p>
        <h2>{connector.name}</h2>
        <StatusPill tone={statusTone(connector.status)}>
          {statusLabels[connector.status]}
        </StatusPill>
      </header>
      <p>{connector.description}</p>
      <dl className="admin-settings__facts admin-settings__facts--detail">
        <div>
          <dt>Provider / mode</dt>
          <dd>
            {connector.provider} / {connector.mode}
          </dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{connector.owner ?? "Not assigned"}</dd>
        </div>
        <div>
          <dt>Safe identifier</dt>
          <dd>{connector.safeIdentifierAlias ?? "Not configured"}</dd>
        </div>
        <div>
          <dt>Callback</dt>
          <dd>{connector.callbackUrl ?? "Not configured"}</dd>
        </div>
        <div>
          <dt>Configuration version</dt>
          <dd>{connector.configurationVersion ?? "Not configured"}</dd>
        </div>
        <div>
          <dt>Last diagnostic</dt>
          <dd>{connector.lastDiagnostic?.outcome ?? "Not run"}</dd>
        </div>
      </dl>
      <section>
        <h3>Required secret references</h3>
        {connector.requiredSecrets.length === 0 ? (
          <p>No secret reference is declared for this connector.</p>
        ) : (
          <ul>
            {connector.requiredSecrets.map((secret) => (
              <li key={secret.name}>
                <code>{secret.name}</code> — {secret.status}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h3>Capabilities</h3>
        <ul>
          {connector.capabilities.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
      <section>
        <h3>Recent safe events</h3>
        {detail.recentEvents.length === 0 ? (
          <p>No events recorded.</p>
        ) : (
          <ul>
            {detail.recentEvents.map((event) => (
              <li key={event.eventId}>{event.type}</li>
            ))}
          </ul>
        )}
      </section>
      <ConnectorConfigurationPanel
        key={`${connector.connectorId}:${connector.configurationVersion ?? 0}`}
        detail={detail}
        gateways={gateways}
        canPatchMetadata={canAccessPortalArea(
          subject,
          "admin-settings",
          "write",
        )}
        canRequestRotation={canAccessPortalArea(
          subject,
          "admin-settings",
          "manage",
        )}
        notice={notice}
        onMutationConfirmed={onMutationConfirmed}
        onRefreshRequested={onRefreshRequested}
      />
    </article>
  );
}

export function AdminConnectorSettingsRoute({
  pathname,
  subject,
  gateways,
}: AdminConnectorSettingsRouteProps) {
  const route = resolveAdminConnectorSettingsRoute(pathname);
  const authorized = canReadConnectorSettings(subject);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  const [mutationNotice, setMutationNotice] = useState<string | null>(null);
  const visibleState = currentLoadState(
    state,
    subject,
    pathname,
    gateways,
    refreshEpoch,
  );

  useEffect(() => {
    setMutationNotice(null);
    setRefreshEpoch(0);
  }, [gateways, pathname, subject]);

  useEffect(() => {
    const controller = new AbortController();
    const generation = captureProtectedStateGeneration();
    const clear = () => {
      controller.abort();
      setState({ kind: "loading" });
    };
    const unregister = registerProtectedStateCleaner(
      "admin-connector-settings",
      clear,
    );
    setState({ kind: "loading" });
    if (
      !authorized ||
      subject === null ||
      !gateways ||
      !route ||
      route.kind === "invalid"
    ) {
      return () => {
        controller.abort();
        unregister();
      };
    }

    const loadIdentity: LoadIdentity = {
      subject,
      pathname,
      gateways,
      refreshEpoch,
    };

    const request =
      route.kind === "detail"
        ? gateways.readConnector(route.connectorId, {
            signal: controller.signal,
          })
        : gateways.readRegistry({ signal: controller.signal });
    void request.then(
      (value) => {
        if (
          controller.signal.aborted ||
          !isProtectedStateGenerationCurrent(generation)
        )
          return;
        if (route.kind === "detail" && isConnectorDetailSnapshot(value)) {
          setState({ kind: "detail", value, ...loadIdentity });
        } else if (
          route.kind !== "detail" &&
          isConnectorRegistrySnapshot(value)
        ) {
          setState({ kind: "registry", value, ...loadIdentity });
        } else {
          setState({ kind: "error", ...loadIdentity });
        }
      },
      () => {
        if (
          !controller.signal.aborted &&
          isProtectedStateGenerationCurrent(generation)
        )
          setState({ kind: "error", ...loadIdentity });
      },
    );
    return () => {
      controller.abort();
      unregister();
    };
  }, [authorized, gateways, pathname, refreshEpoch, subject]);

  if (!route || route.kind === "invalid") {
    return (
      <SettingsFrame>
        <ContentState kind="error" title="Settings route not found" />
      </SettingsFrame>
    );
  }
  if (!authorized) {
    return (
      <SettingsFrame>
        <ContentState kind="warning" title="Access denied">
          <p>Connector settings require an authorized admin account.</p>
        </ContentState>
      </SettingsFrame>
    );
  }
  if (!gateways) {
    return (
      <SettingsFrame>
        <ContentState kind="warning" title="Settings connection unavailable">
          <p>
            No production settings gateway is configured. No synthetic data was
            substituted.
          </p>
        </ContentState>
      </SettingsFrame>
    );
  }

  return (
    <SettingsFrame>
      <CompatibilityNotice route={route} />
      {visibleState.kind === "loading" && (
        <ContentState kind="loading" title="Loading connector settings" />
      )}
      {visibleState.kind === "error" && (
        <ContentState
          kind="error"
          title="Connector settings could not be verified"
        />
      )}
      {visibleState.kind === "registry" && (
        <ConnectorCards registry={visibleState.value} />
      )}
      {visibleState.kind === "detail" && (
        <ConnectorDetail
          detail={visibleState.value}
          subject={subject!}
          gateways={gateways}
          notice={mutationNotice}
          onMutationConfirmed={(message) => {
            setMutationNotice(message);
            setRefreshEpoch((current) => current + 1);
          }}
          onRefreshRequested={() => {
            setMutationNotice(null);
            setRefreshEpoch((current) => current + 1);
          }}
        />
      )}
    </SettingsFrame>
  );
}
